(() => {
  'use strict';

  const root = document.getElementById('BerliozaSalesAssistant');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const launcher = root.querySelector('.bb-assistant__launcher');
  const panel = root.querySelector('.bb-assistant__panel');
  const closeButton = root.querySelector('.bb-assistant__close');
  const messages = root.querySelector('.bb-assistant__messages');
  const form = root.querySelector('.bb-assistant__form');
  const input = root.querySelector('#BerliozaAssistantInput');
  const welcome = root.querySelector('.bb-assistant__welcome');
  const quickReplies = root.querySelector('.bb-assistant__quick-replies');

  if (!launcher || !panel || !closeButton || !messages || !form || !input || !quickReplies) return;

  const urls = {
    cart: root.dataset.cartUrl || '/cart',
    account: root.dataset.accountUrl || '/account',
    contact: root.dataset.contactUrl || '/pages/contact-us',
    returns: root.dataset.returnsUrl || '/policies/refund-policy',
    bestsellers: root.dataset.bestSellersUrl || '/collections/best-sellers',
    newdrop: root.dataset.newDropUrl || '/collections/berlioza-coming-soon',
    shopall: root.dataset.shopAllUrl || '/collections/all',
    journal: root.dataset.journalUrl || '/blogs/news'
  };

  const pageType = root.dataset.pageType || '';
  const productTitle = root.dataset.productTitle || '';
  const currency = root.dataset.currency || 'USD';
  const freeShippingThreshold = Number.parseInt(root.dataset.freeShippingThreshold, 10) || 10000;

  if (welcome && pageType === 'product' && productTitle) {
    welcome.textContent = `Questions about ${productTitle}? I can help with fit, shipping, returns, or checkout.`;
  }

  const track = (eventName, details = {}) => {
    const storefrontEventName = `berlioza_${eventName}`;
    const shopifyEventName = `berlioza:${eventName}`;

    document.dispatchEvent(new CustomEvent(`berlioza:${eventName}`, { detail: details }));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: storefrontEventName, ...details });

    if (window.Shopify?.analytics?.publish) {
      try {
        const published = window.Shopify.analytics.publish(shopifyEventName, details);
        if (published && typeof published.catch === 'function') {
          published.catch(() => {});
        }
      } catch (_error) {
        // Analytics must never interfere with the shopping experience.
      }
    }
  };

  const scrollMessages = () => {
    messages.scrollTop = messages.scrollHeight;
  };

  const addMessage = (role, text, actions = []) => {
    const wrapper = document.createElement('div');
    wrapper.className = `bb-assistant__message bb-assistant__message--${role}`;

    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    wrapper.appendChild(paragraph);
    messages.appendChild(wrapper);

    if (actions.length) {
      const actionRow = document.createElement('div');
      actionRow.className = 'bb-assistant__actions';

      actions.forEach((action) => {
        const link = document.createElement('a');
        link.className = 'bb-assistant__action';
        link.href = action.url;
        link.textContent = action.label;
        link.addEventListener('click', () => track('assistant_cta', { action: action.key || action.label }));
        actionRow.appendChild(link);
      });

      messages.appendChild(actionRow);
    }

    scrollMessages();
  };

  const topics = {
    bestsellers: {
      prompt: 'Show me the best sellers',
      answer: 'Start with the pieces customers choose most. The Best Sellers edit is the fastest route to proven BerliozaBoyz style.',
      actions: [
        { label: 'Shop Best Sellers', url: urls.bestsellers, key: 'best_sellers' },
        { label: 'Shop All', url: urls.shopall, key: 'shop_all' }
      ]
    },
    sizing: {
      prompt: 'How do I choose my size?',
      answer: 'Fit can vary by piece. Check the size and fit details on the product page. If you are between sizes or want personal help, contact us before ordering so we can guide you.',
      actions: [
        { label: 'Contact for Fit Help', url: urls.contact, key: 'fit_help' },
        { label: 'Keep Shopping', url: urls.shopall, key: 'shop_all' }
      ]
    },
    shipping: {
      prompt: 'How does shipping work?',
      answer: 'U.S. shipping is $7.95 under $100 and free at $100+. Canada shipping is $14.95 under $150 and free at $150+. Other international shipping is $24.95 under $150, $14.95 from $150–$199.99, and free at $200+. Orders are generally processed in 1–3 business days. Duties, import taxes, and customs charges may apply to international orders.',
      actions: [
        { label: 'Build My Cart', url: urls.bestsellers, key: 'build_cart' },
        { label: 'View Cart', url: urls.cart, key: 'view_cart' }
      ]
    },
    returns: {
      prompt: 'What is the return policy?',
      answer: 'Eligible regular-priced items can be returned within 30 days of delivery when unused and in original condition. Return shipping and a 5% restocking fee apply. Review the full policy before starting a return.',
      actions: [
        { label: 'Read Return Policy', url: urls.returns, key: 'return_policy' },
        { label: 'Contact Support', url: urls.contact, key: 'return_support' }
      ]
    },
    order: {
      prompt: 'I need help with an order',
      answer: 'Sign in to review your order information, or contact support with your order number and the email used at checkout. That helps the team assist you faster.',
      actions: [
        { label: 'My Account', url: urls.account, key: 'account' },
        { label: 'Contact Support', url: urls.contact, key: 'order_support' }
      ]
    },
    newdrop: {
      prompt: 'What is new?',
      answer: 'Explore the newest BerliozaBoyz pieces and secure your favorites while your size is available.',
      actions: [
        { label: 'Shop The Drop', url: urls.newdrop, key: 'new_drop' },
        { label: 'Best Sellers', url: urls.bestsellers, key: 'best_sellers' }
      ]
    },
    checkout: {
      prompt: 'Can you help me check out?',
      answer: 'Review your size, quantity, shipping destination and contact details in the cart, then continue through Shopify’s secure checkout.',
      actions: [
        { label: 'View Cart', url: urls.cart, key: 'view_cart' },
        { label: 'Contact Support', url: urls.contact, key: 'checkout_support' }
      ]
    },
    style: {
      prompt: 'How should I style it?',
      answer: 'Build around one statement piece, keep the supporting layers clean, and let the fit carry the look. The Streetwear Journal has more ideas for confident everyday styling.',
      actions: [
        { label: 'Read Style Guides', url: urls.journal, key: 'journal' },
        { label: 'Shop Best Sellers', url: urls.bestsellers, key: 'best_sellers' }
      ]
    },
    contact: {
      prompt: 'I want to talk to someone',
      answer: 'The support team can help with product, fit, order and delivery questions. Include your order number when your question is about an existing purchase.',
      actions: [
        { label: 'Contact Support', url: urls.contact, key: 'contact' }
      ]
    }
  };

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const answerCart = async (showPrompt = true) => {
    if (showPrompt) addMessage('user', 'What is in my cart?');

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();

      if (!cart.item_count) {
        addMessage('bot', 'Your cart is empty. Start with Best Sellers to find the strongest pieces first.', [
          { label: 'Shop Best Sellers', url: urls.bestsellers, key: 'best_sellers' }
        ]);
      } else if (cart.total_price >= freeShippingThreshold) {
        addMessage('bot', `You have ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in your cart, totaling ${formatMoney(cart.total_price)}. Free U.S. standard shipping is unlocked.`, [
          { label: 'Review Cart', url: urls.cart, key: 'view_cart' }
        ]);
      } else {
        const remaining = freeShippingThreshold - cart.total_price;
        addMessage('bot', `You have ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in your cart, totaling ${formatMoney(cart.total_price)}. Add ${formatMoney(remaining)} more to reach free U.S. standard shipping.`, [
          { label: 'Add a Best Seller', url: urls.bestsellers, key: 'free_shipping_progress' },
          { label: 'Review Cart', url: urls.cart, key: 'view_cart' }
        ]);
      }
    } catch (_error) {
      addMessage('bot', 'Open your cart to review items, quantities and shipping progress.', [
        { label: 'View Cart', url: urls.cart, key: 'view_cart' }
      ]);
    }
  };

  const detectTopic = (question) => {
    const normalized = question.toLowerCase();
    const rules = [
      ['shipping', /ship|delivery|deliver|international|free ship|customs|duty|duties/],
      ['returns', /return|refund|exchange|send back|restock/],
      ['sizing', /size|sizing|fit|fits|measure|measurement|small|medium|large|xl|xxl/],
      ['order', /order|track|tracking|status|package|missing|lost/],
      ['cart', /cart|free shipping progress|how much more/],
      ['checkout', /checkout|check out|pay|payment|secure/],
      ['bestsellers', /best|popular|recommend|top seller|what should i buy|what to buy/],
      ['newdrop', /new|drop|restock|latest|coming soon/],
      ['style', /style|wear|outfit|look|match/],
      ['contact', /human|person|someone|contact|email|support|talk|agent/]
    ];

    const match = rules.find(([, pattern]) => pattern.test(normalized));
    return match ? match[0] : 'unknown';
  };

  const answerTopic = (topic, options = {}) => {
    if (topic === 'cart') {
      answerCart(options.showPrompt !== false);
      track('assistant_question', { topic: 'cart' });
      return;
    }

    const content = topics[topic];
    if (!content) {
      addMessage('bot', 'I can answer questions about sizing, shipping, returns, orders, styling and checkout. For anything else, the support team is ready to help.', [
        { label: 'Contact Support', url: urls.contact, key: 'contact' },
        { label: 'Shop Best Sellers', url: urls.bestsellers, key: 'best_sellers' }
      ]);
      track('assistant_question', { topic: 'unknown' });
      return;
    }

    if (options.showPrompt !== false) addMessage('user', content.prompt);
    addMessage('bot', content.answer, content.actions);
    track('assistant_question', { topic });
  };

  const openPanel = () => {
    panel.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    track('assistant_open', { page_type: pageType || 'unknown' });
    window.setTimeout(() => input.focus(), 0);
  };

  const closePanel = () => {
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    launcher.focus();
  };

  launcher.addEventListener('click', () => {
    if (panel.hidden) openPanel();
    else closePanel();
  });

  closeButton.addEventListener('click', closePanel);

  quickReplies.addEventListener('click', (event) => {
    const button = event.target.closest('[data-topic]');
    if (!button) return;
    answerTopic(button.dataset.topic);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const question = input.value.trim();
    if (!question) return;

    addMessage('user', question);
    input.value = '';
    answerTopic(detectTopic(question), { showPrompt: false });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) closePanel();
  });
})();
